import { Button, Card, Table, TableColumnsType } from 'antd'
import { TableProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  GetPersonByIdnpResponse,
  GetPersonListItemByDataResponse
} from 'src/api/requests'
import { TransferModal } from 'src/features/persons/components/Transfer'
import { useStyles } from 'src/features/persons/styles'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formatIdnp } from 'src/utils'

type Props = {
  isLoading?: boolean
  data?: GetPersonListItemByDataResponse[]
  onCancel?: () => void
  onAddClick?: (id: string) => void
  /** When true, the action is a plain "Select" that always calls onAddClick
   *  (no transfer, no navigate) — used when picking a person to fill a form. */
  selectMode?: boolean
} & TableProps

const DEFAULT_USER_LIST_PER_PAGE = 5

export const SearchPersonTable = ({
  data,
  isLoading,
  onAddClick,
  onCancel,
  selectMode,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const { userData } = useUser()
  const [selectedPage, setSelectedPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_USER_LIST_PER_PAGE)

  const [modalState, setModalState] = useState<{
    isOpen: boolean
    data?: GetPersonByIdnpResponse | null
  }>({ data: null, isOpen: false })

  const paginatedSelectedUsers = data?.slice(
    (selectedPage - 1) * pageSize,
    selectedPage * pageSize
  )

  const navigate = useNavigate()

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'lastName',
        title: t('form.last_name'),
        dataIndex: 'lastName'
      },
      {
        key: 'firstName',
        title: t('form.first_name'),
        dataIndex: 'firstName'
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: 'idnp',
        render: (_, data) => (data?.idnp ? formatIdnp(data.idnp) : '-')
      },
      {
        key: 'birth_date',
        title: t('form.birth_date'),
        render: (_, { birthDate }) =>
          dayjs(birthDate).format(dayMonthYearFormatWithDot),
        align: 'center'
      },
      {
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: (person) => {
          // In select mode the action always just picks the person — never a
          // transfer request or a navigation to the details page.
          if (selectMode) {
            return (
              <Button
                type="link"
                onClick={() => onAddClick && onAddClick(person.id)}
              >
                {t('common.select')}
              </Button>
            )
          }

          const canTransfer =
            person?.institution?.id !== userData?.currentLegalEntityId

          const onClick = () => {
            if (canTransfer)
              return setModalState({
                isOpen: true,
                data: person
              })
            if (onAddClick) return onAddClick(person.id)

            navigate(paths.persons.details(person.id))
            onCancel && onCancel()
          }

          return (
            <Button type="link" onClick={onClick}>
              {t(canTransfer ? 'persons.patient_transfer' : 'common.access')}
            </Button>
          )
        }
      }
    ],
    [t, selectMode, onAddClick]
  )

  return (
    <Card className={styles.personsCard}>
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={paginatedSelectedUsers}
        columns={columns}
        pagination={{
          current: selectedPage,
          pageSize,
          total: data?.length,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
          showTotal: (total) => `${t('common.total')} ${total}`,
          onChange: (page) => setSelectedPage(page),
          hideOnSinglePage: true
        }}
        scroll={{ x: 'max-content' }}
        {...props}
      />

      {modalState?.isOpen && modalState?.data && (
        <TransferModal data={modalState.data} onCancel={onCancel} />
      )}
    </Card>
  )
}
